// Mouth lock vocabulary (IDS-020), shared by the identity-reference reader and
// the cast appearance panel. Kept tiny and dependency-free on purpose: the
// identity-reference badge lives in the eagerly loaded Director View, while
// the cast panel (castAppearance.ts) is lazy-loaded, and this is the only part
// both need.

/**
 * The per-scene mouth lock the identity intent (v6) carries for each person:
 * - `closed_never_seen`: no photo ever showed their teeth → lips closed;
 * - `closed_this_shot`: teeth were seen elsewhere, but this scene's photo is a
 *   closed mouth → lips closed for this shot only (no "never seen" claim);
 * - `teeth_as_source`: this photo shows an open smile → keep their real teeth.
 */
export type MouthLockV2 =
  | "closed_never_seen"
  | "closed_this_shot"
  | "teeth_as_source";

/** The v1 lock every intent still carries for older composers. */
export type LegacyMouthLock = "closed" | "as_source";

export const MOUTH_LOCK_V2_LABELS: Record<MouthLockV2, string> = {
  closed_never_seen: "Lips closed — teeth never seen",
  closed_this_shot: "Lips closed in this shot",
  teeth_as_source: "Real teeth shown — preserve",
};

export function parseMouthLockV2(value: unknown): MouthLockV2 | null {
  return typeof value === "string" &&
    Object.prototype.hasOwnProperty.call(MOUTH_LOCK_V2_LABELS, value)
    ? (value as MouthLockV2)
    : null;
}

export function parseLegacyMouthLock(value: unknown): LegacyMouthLock | null {
  return value === "closed" || value === "as_source" ? value : null;
}

export function mouthLockV2Label(lock: MouthLockV2): string {
  return MOUTH_LOCK_V2_LABELS[lock];
}
